import {
  ApolloCache,
  MutationUpdaterFunction,
  NormalizedCacheObject,
} from '@apollo/client';

import {
  CreateReactionMutation,
  CreateReactionMutationVariables,
  IdentityFragment,
  ReactionSummaryFragment,
  ReactionSummaryFragmentDoc,
} from '@spotify/confidence-graphql';

import { reactionSummaryNameFromResource } from '../../utils';

export function createReactionCacheUpdate(
  currentIdentity: IdentityFragment | null | undefined,
): MutationUpdaterFunction<
  CreateReactionMutation,
  CreateReactionMutationVariables,
  Record<string, unknown>,
  ApolloCache<NormalizedCacheObject>
> {
  return (cache, _result, updateOptions) => {
    if (!currentIdentity) {
      return;
    }

    const variables = updateOptions.variables;
    if (!variables) {
      return;
    }

    const { resource, emoji } = variables.input;

    const summaryName = reactionSummaryNameFromResource(resource);

    const cacheId = cache.identify({
      __typename: 'CollaborationV1ReactionSummary',
      name: summaryName,
    });

    cache.updateFragment<ReactionSummaryFragment>(
      {
        id: cacheId,
        fragment: ReactionSummaryFragmentDoc,
        fragmentName: 'ReactionSummary',
      },
      existingSummary => {
        if (!existingSummary) {
          return existingSummary;
        }

        const existingReactions = existingSummary.emojiReactions || [];
        const existingIndex = existingReactions.findIndex(
          entry => entry.key === emoji,
        );

        if (existingIndex >= 0) {
          // Update existing emoji reaction
          const existingEntry = existingReactions[existingIndex];
          const existingValue = existingEntry.value;

          return {
            ...existingSummary,
            emojiReactions: existingReactions.map((entry, index) => {
              if (index !== existingIndex) {
                return entry;
              }

              return {
                ...entry,
                value: {
                  ...existingValue,
                  reactionCount: (existingValue?.reactionCount || 0) + 1,
                  identities: [
                    ...(existingValue?.identities || []),
                    currentIdentity,
                  ],
                },
              };
            }),
          };
        }

        // Add new emoji reaction
        return {
          ...existingSummary,
          emojiReactions: [
            ...existingReactions,
            {
              key: emoji,
              value: {
                emoji,
                reactionCount: 1,
                identities: [currentIdentity],
              },
            },
          ],
        };
      },
    );
  };
}
